import { gsap, ScrollTrigger } from "@/composeables/gsap"

const isHidden = (el) => getComputedStyle(el).display === "none"

const REVEAL = { autoAlpha: 1, y: 0, duration: 1, ease: "expo.out" }

// Fisher–Yates auf einer Kopie.
const shuffled = (arr) => {
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
}

// Client-seitiges Mischen der Practitioner-Karten. Der Server mischt zwar
// bereits (.shuffle()), aber die Seite ist Blitz-gecached → die Reihenfolge
// friert ein und wäre für alle gleich. Deshalb hier beim Laden neu mischen.
//
// Die sichtbare/versteckte Aufteilung (show-more „Gesamtes Team": erste 4
// sichtbar, Rest per v-show=isVisible(index) versteckt) MUSS erhalten bleiben:
// die per Index gebundene v-show-Sichtbarkeit hängt an der DOM-Position. Wir
// mischen daher sichtbare Wrapper untereinander in den vorderen Slots und
// versteckte untereinander in den hinteren — die Karten werden voll gemischt,
// ohne dass eine versteckte Karte in einen sichtbaren Slot rutscht (Lücke).
const shufflePractitioners = (section) => {
    // Server-Sortier-Absicht (aus sectionPractitioners.twig data-Attributen):
    // shuffle=false => feste Server-Reihenfolge NICHT anfassen (z.B. Team-Seite
    // mit sortByPositionRank). shuffleFrom => die ersten N (sichtbaren) Karten
    // bleiben stehen (z.B. Chefchirurg vorne), nur der Rest wird gemischt.
    if (section.dataset.shuffle === "false") return
    const shuffleFrom = Math.max(0, parseInt(section.dataset.shuffleFrom || "0", 10) || 0)

    const main = section.querySelector(".section-practitioners__main")
    // Einzel-Arzt (--single) enthält Karte + Detail-Ansicht → nicht mischen.
    if (!main || main.classList.contains("section-practitioners__main--single")) return

    const wrappers = Array.from(main.children)
    if (wrappers.length < 2) return

    // Ein Wrapper (span.d:contents) gilt als sichtbar, wenn seine Karte nicht
    // per v-show ausgeblendet ist.
    const isWrapperVisible = (w) => {
        const item = w.querySelector(".section-practitioners__item")
        return item ? !isHidden(item) : true
    }

    const visible = wrappers.filter(isWrapperVisible)
    const hidden = wrappers.filter((w) => !isWrapperVisible(w))

    // Die ersten `shuffleFrom` (sichtbaren) Karten bleiben fix vorne, nur der
    // Rest der Sichtbaren wird gemischt. Versteckte werden komplett gemischt.
    const fixed = visible.slice(0, shuffleFrom)
    const shuffleVisible = visible.slice(shuffleFrom)
    const ordered = [...fixed, ...shuffled(shuffleVisible), ...shuffled(hidden)]
    ordered.forEach((w) => main.appendChild(w))
}

export const init = () => {
    const sections = gsap.utils.toArray(".section-practitioners")

    sections.forEach((section) => {
        // Slider-Modus (showMoreMode = slider): der <card-slider> (Swiper)
        // steuert Layout + Sichtbarkeit der Karten selbst. Kein Grid-Shuffle
        // und KEIN GSAP-Reveal (autoAlpha:0 würde mit Swipers Positionierung
        // kollidieren und Slides unsichtbar lassen).
        if (section.querySelector(".section-practitioners__slider")) return

        // Bühne-Variante: eigenes Handling (Pin + Durchscroll) in
        // sectionPractitionersStage.js. Kein Grid-Shuffle/-Reveal.
        if (section.classList.contains("section-practitioners--stage")) return

        // Erst mischen, dann die (jetzt in neuer DOM-Reihenfolge vorliegenden)
        // Karten für den Reveal einsammeln.
        shufflePractitioners(section)

        const items = Array.from(section.querySelectorAll(":scope .section-practitioners__item"))
        if (!items.length) return

        gsap.set(items, { autoAlpha: 0, y: 60 })

        // Blendet die (reihenweise vom batch übergebenen) Karten gestaffelt ein.
        const reveal = (batch) => {
            const toAnimate = batch.filter(it => !it.dataset.revealed)
            if (!toAnimate.length) return
            toAnimate.forEach(it => { it.dataset.revealed = "1" })
            gsap.to(toAnimate, { ...REVEAL, stagger: 0.12, overwrite: true })
        }

        // Bindet alle aktuell sichtbaren, noch nicht beobachteten Karten an die
        // Scroll-Reveal-Logik an: jede Grid-Reihe fährt ein, sobald sie in den
        // Viewport scrollt (batch bündelt gleichzeitig eintretende Karten =
        // eine Reihe), bereits sichtbare Karten feuern sofort. Vorher lag ein
        // einzelner "center bottom"-Trigger auf dem ganzen Grid → bei vielen
        // Ärzten blieben die oberen Reihen viel zu lange unsichtbar.
        const observe = () => {
            const fresh = items.filter(it => !it.dataset.observed && !isHidden(it))
            if (!fresh.length) return
            fresh.forEach(it => { it.dataset.observed = "1" })
            ScrollTrigger.batch(fresh, { start: "top 85%", onEnter: reveal })
        }

        observe()

        // Show-more ("Gesamtes Team") und Breakpoint-Wechsel (Einzel-Arzt:
        // mobil Karte / ab sm Detail-Ansicht) machen weitere Karten sichtbar –
        // die an dieselbe Reveal-Logik anbinden statt sie sofort (und meist
        // außerhalb des Viewports) durchzuanimieren.
        const observeNew = () => {
            observe()
            ScrollTrigger.refresh()
        }

        let resizeRaf
        window.addEventListener("resize", () => {
            cancelAnimationFrame(resizeRaf)
            resizeRaf = requestAnimationFrame(observeNew)
        })

        const showMoreButton = section.querySelector(".section-practitioners__header button")
        if (showMoreButton) {
            showMoreButton.addEventListener("click", () => {
                // Warten, bis Vue v-show auf den neuen Karten umgelegt hat.
                requestAnimationFrame(() => requestAnimationFrame(observeNew))
            })
        }
    })
}

export default init
